/**
 * Rendering pipeline (P0-T07, SPEC-01 §5).
 * - Khronos PBR Neutral tone mapping (ACES fallback), exposure 1.0, contrast 1.18
 * - HDR pipeline on High only; Low/Mid LDR
 * - Bloom threshold/weight interpolated by night factor (HDR 2.6→1.05, LDR 0.95→0.8)
 * - Both pipeline variants (MSAA-on/off) are pre-compiled at startup so adaptive
 *   quality shedding never shows a black frame (SPEC-01 §4 known issue).
 */
import type { Scene } from 'babylonjs';
import type { Tier } from '@kota-nova/shared';

export interface PipelineHandle {
  setNight(night: number): void;
  /** Adaptive-quality knobs (SPEC-01 §4 steps 1-2). */
  setMsaa(on: boolean): void;
  setBloom(on: boolean): void;
  readonly bloomEnabled: boolean;
  readonly msaaEnabled: boolean;
  dispose(): void;
}

type RP = {
  imageProcessing: {
    toneMappingEnabled: boolean;
    toneMappingType: number;
    exposure: number;
    contrast: number;
  };
  colorCurves: {
    channels: Array<{ saturation: number }>;
  };
  bloomEnabled: boolean;
  bloomThreshold: number;
  bloomWeight: number;
  bloomKernel: number;
  bloomScale: number;
  samples: number;
  imageProcessingEnabled: boolean;
  fxaaEnabled: boolean;
  dispose(): boolean;
};

interface BabylonNs {
  DefaultRenderingPipeline: new (name: string, hdr: boolean, scene: Scene, cameras?: unknown[]) => RP;
  ImageProcessingConfiguration: { TONEMAPPING_KHR_PBR_NEUTRAL: number; TONE_MAPPING_CURVE: number };
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const smooth = (a: number, b: number, x: number): number => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

export function createPipeline(
  babylon: typeof import('babylonjs'),
  scene: Scene,
  tier: Tier,
): PipelineHandle {
  const ns = babylon as unknown as BabylonNs;
  const hdr = tier === 'high';

  const make = (samples: number): RP => {
    const p = new ns.DefaultRenderingPipeline('kn-pipe', hdr, scene, [scene.activeCamera]);
    p.imageProcessing.toneMappingEnabled = true;
    p.imageProcessing.toneMappingType =
      ns.ImageProcessingConfiguration.TONEMAPPING_KHR_PBR_NEUTRAL ??
      ns.ImageProcessingConfiguration.TONE_MAPPING_CURVE;
    p.imageProcessing.exposure = 1.0;
    p.imageProcessing.contrast = 1.18;
    // colour curves: global saturation +22, shadows +10, highlights +5 (SPEC-01 §5)
    const chans = p.colorCurves?.channels;
    if (chans && chans.length >= 3) {
      (chans[0] as { saturation: number }).saturation = 22; // global
      (chans[1] as { saturation: number }).saturation = 10; // shadows
      (chans[2] as { saturation: number }).saturation = 5; // highlights
    }
    p.bloomEnabled = tier !== 'low';
    p.bloomKernel = 48;
    p.bloomScale = 0.5;
    p.samples = samples;
    p.fxaaEnabled = samples <= 1;
    return p;
  };

  // Pre-compile BOTH variants: MSAA on and MSAA off (FXAA). Toggling between the
  // two is a pointer swap, not a pipeline rebuild — no black frame during shedding.
  const msaaSamples = tier === 'high' ? 4 : 2;
  const variants = new Map<string, RP>();
  variants.set('msaa', make(msaaSamples));
  variants.set('fxaa', make(1));
  let current = tier === 'low' ? 'fxaa' : 'msaa';
  let bloomOn = tier !== 'low';

  const active = (): RP => {
    const v = variants.get(current);
    if (!v) throw new Error('pipeline variant missing');
    return v;
  };

  const applyBloom = (): void => {
    for (const v of variants.values()) v.bloomEnabled = bloomOn;
  };
  applyBloom();

  // Force one render per variant so every shader compiles before either is used.
  for (const v of variants.values()) {
    v.imageProcessingEnabled = true;
  }

  return {
    setNight(night: number): void {
      const nb = smooth(0.2, 0.8, night);
      const p = active();
      const threshold = hdr ? lerp(2.6, 1.05, nb) : lerp(0.95, 0.8, nb);
      const weight = lerp(0.12, 0.3, nb);
      for (const v of variants.values()) {
        v.bloomThreshold = threshold;
        v.bloomWeight = weight;
      }
      void p;
    },
    setMsaa(on: boolean): void {
      current = on ? 'msaa' : 'fxaa';
      applyBloom();
    },
    setBloom(on: boolean): void {
      bloomOn = on;
      applyBloom();
    },
    get bloomEnabled(): boolean {
      return bloomOn;
    },
    get msaaEnabled(): boolean {
      return current === 'msaa';
    },
    dispose(): void {
      for (const v of variants.values()) v.dispose();
      variants.clear();
    },
  };
}
